import React, { useState, useEffect, useRef } from "react";
import AllDepartments from "../AllDepartments";
import axios from 'axios';
import { G_API, PAGE_LIMIT } from '../../constants/globalVar';
import PageSlider from '../PageSlider';

const departmentsList = [
    { name: "Hta-Hnaung Centre for Academic Studies", code: "HCS", description: "The Academic Centre offers a range of educational opportunities, including undergraduate diploma programmes and vocational short courses. Our current offering is the eight-month Diploma in Community Development. Additionally, the centre offers the Kann Let Scholarship Mentoring Programme, dedicated to supporting students in their pursuit of higher education both at home and abroad."},
    { name: "Academic Affairs and Quality Control Department", code: "AQCD", description: "The Department oversees an essential quality control framework outlining standards and guidelines for academic affairs and projects under Ah Nyar Institute." },
    { name: "Programme Support Department", code: "PSD", description: "This department is a unified support department for all units under the Ah Nyar Institute. Technically, the department functions are divided into <br/>- Human Resources Management<br/>- Financial Management<br/>- Administrative and Logistic support" }
];

const DepartmentPage = () => {
    const [activeDept, setActiveDept] = useState(() => {
        const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
        const index = departmentsList.findIndex(dept => dept.code === hash);
        return index !== -1 ? index : 0;
    });

    const [selectedDeptCode, setSelectedDeptCode] = useState(() => {
        return departmentsList[activeDept]?.code || '';
    });

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(false);
    
    const abortControllerRef = useRef(null);

    useEffect(() => {
        if (window.location.hash !== `#${selectedDeptCode}`) {
            window.location.hash = selectedDeptCode;
        }
    }, []);

    useEffect(() => {
        if (!selectedDeptCode) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        const fetchDepartmentArticles = async () => {
            try {
                setLoading(true);
                const response = await axios.post(`${G_API}department-articles/LIST`, {
                    page: currentPage,
                    limit: PAGE_LIMIT,
                    department: selectedDeptCode
                }, { signal: controller.signal });

                setItems(response.data.DATAS || []);
                setCurrentPage(response.data.CURRENT_PAGE || 1);
                setLastPage(response.data.LAST_PAGE || 1);
                setLoading(false);
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                console.error("Error fetching department articles:", error);
                setLoading(false);
            }
        };

        fetchDepartmentArticles();

        return () => {
            controller.abort();
        };
    }, [selectedDeptCode, currentPage]);

    const selectDept = (index, deptCode) => {
        if (deptCode === selectedDeptCode) return;
        setActiveDept(index);
        setSelectedDeptCode(deptCode);
        setCurrentPage(1); 
        window.location.hash = deptCode;
    };

    return (
        <>
            <div id="header-carousel" className="carousel slide" data-bs-ride="carousel">
                <div className="carousel-inner">
                    <PageSlider inputType="Department" />
                </div>
            </div>
            
            <section className="py-4 px-2" style={{ backgroundColor: "#d4f7e6", overflowX: "hidden" }}>
                <div className="container-fluid">
                    <h3 className="text-center my-2">Our Departments</h3>
                    
                    <div className="row justify-content-center">
                        {departmentsList.map((dept, index) => (
                            <div
                                className={`col-12 col-md-auto card m-2 p-4 d-flex flex-column ${activeDept === index ? 'bg-light border-success' : ''}`}
                                style={{ width: '300px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                key={dept.code}
                                onClick={() => selectDept(index, dept.code)}
                            >
                                <div className="flex-grow-1">
                                    <h4>{dept.name}</h4>
                                    <div dangerouslySetInnerHTML={{ __html: dept.description }} />
                                </div>

                                <div className="d-flex justify-content-center mt-auto pt-3">
                                    <button className={`btn btn-md rounded-pill px-4 py-1 fw-light ${activeDept === index ? 'btn-primary text-white' : 'btn-outline-primary'}`}>
                                        {activeDept === index ? 'Viewing' : 'View More'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="departments" className="container-xxl my-5 py-5">
                <div className="container my-2 py-2" style={{ minHeight: '200px' }}> 
                    {loading ? (
                        <div className="text-center my-4 py-5">
                            <div className="spinner-border text-success" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row animate-fade-in">
                            {items.length > 0 ? items.map(item => (
                                <div className="col-12 mb-4" key={item._id}>
                                    <AllDepartments item={item} />
                                </div>
                            )) : (
                                <div className="text-center text-muted my-4">No Articles Yet</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div className={`d-flex justify-content-center mt-4 ${lastPage <= 1 ? 'd-none' : ''}`}>
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 || loading ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => !loading && setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                    Previous
                                </button>
                            </li>

                            {Array.from({ length: lastPage }, (_, i) => i + 1).map(page => (
                                <li key={page} className={`page-item ${page === currentPage ? "active" : ""} ${loading ? "disabled" : ""}`}>
                                    <button className="page-link" onClick={() => !loading && setCurrentPage(page)}>
                                        {page}
                                    </button>
                                </li>
                            ))}

                            <li className={`page-item ${currentPage === lastPage || loading ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => !loading && setCurrentPage(prev => Math.min(prev + 1, lastPage))}>
                                    Next
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </section>
        </>
    );
}

export default DepartmentPage;